import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiArrowLeft, FiThermometer, FiZap, FiActivity,
  FiCpu, FiMapPin, FiShield, FiCheck, FiSettings, FiUsers,
} from "react-icons/fi";
import { MdOutlineWaterDrop, MdOutlineBathroom } from "react-icons/md";
import "./FacilitySettings.css";

const STORAGE_KEY = "facilityEnabledTabs";

const CONFIGURABLE_TABS = [
  {
    id: "environment",
    label: "Environment",
    icon: FiThermometer,
    color: "#10b981",
    description: "Temperature, humidity, CO₂, PM2.5, PM10 & VOC monitoring across all zones.",
  },
  {
    id: "water",
    label: "Water",
    icon: MdOutlineWaterDrop,
    color: "#3b82f6",
    description: "Water tank levels, pump status, flow rates and leak detection.",
  },
  {
    id: "electrical",
    label: "Electrical & Energy",
    icon: FiZap,
    color: "#f59e0b",
    description: "DG status, fuel history, electrical monitoring and energy analytics.",
  },
  {
    id: "predictive",
    label: "Predictive AI",
    icon: FiCpu,
    color: "#8b5cf6",
    description: "AI-based equipment health scores, failure risk predictions and maintenance alerts.",
  },
  {
    id: "occupancy",
    label: "Occupancy",
    icon: FiUsers,
    color: "#667eea",
    description: "Real-time floor-wise people count, entry/exit trends and building utilization.",
  },
  {
    id: "restroom",
    label: "Rest Room",
    icon: MdOutlineBathroom,
    color: "#0ea5e9",
    description: "Restroom occupancy, supply levels (sanitizer, paper, soap), odor and cleaning schedule.",
  },
  {
    id: "assets",
    label: "Asset Tracking",
    icon: FiMapPin,
    color: "#ec4899",
    description: "Indoor positioning of movable assets on a live warehouse floor plan.",
  },
  {
    id: "security",
    label: "Security",
    icon: FiShield,
    color: "#ef4444",
    description: "CCTV status, fire detection zones and access control logs.",
  },
];

const getStoredTabs = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch (_) {}
  return CONFIGURABLE_TABS.map(t => t.id);
};

const FacilitySettings = () => {
  const navigate = useNavigate();
  const [enabled, setEnabled] = useState(getStoredTabs);
  const [saved, setSaved] = useState(false);

  const toggle = (id) => {
    setEnabled(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
    setSaved(false);
  };

  const handleSave = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(enabled));
    setSaved(true);
    setTimeout(() => navigate("/dashboard/facility"), 800);
  };

  const handleSelectAll = () => {
    setEnabled(CONFIGURABLE_TABS.map(t => t.id));
    setSaved(false);
  };

  const enabledCount = enabled.length;

  return (
    <div className="fs-page">
      {/* Header */}
      <div className="fs-header">
        <button className="fs-back-btn" onClick={() => navigate("/dashboard/facility")}>
          <FiArrowLeft size={18} />
          <span>Back to Dashboard</span>
        </button>
        <div className="fs-header-center">
          <div className="fs-header-icon">
            <FiSettings size={22} color="#667eea" />
          </div>
          <div>
            <h1 className="fs-title">Facility Dashboard Settings</h1>
            <p className="fs-subtitle">Choose which tabs to show on your facility dashboard</p>
          </div>
        </div>
        <div className="fs-header-actions">
          <button className="fs-select-all-btn" onClick={handleSelectAll}>
            Enable All
          </button>
          <button
            className={`fs-save-btn ${saved ? "fs-save-success" : ""}`}
            onClick={handleSave}
            disabled={enabledCount === 0}
          >
            {saved ? <><FiCheck size={16} /> Saved!</> : "Save & Apply"}
          </button>
        </div>
      </div>

      {/* Info strip */}
      <div className="fs-info-strip">
        <span className="fs-info-dot" />
        <span>
          <strong>Overview</strong> tab is always visible. &nbsp;
          {enabledCount} of {CONFIGURABLE_TABS.length} additional tabs enabled.
        </span>
      </div>

      {/* Tab cards */}
      <div className="fs-cards-grid">
        {/* Overview – always on, non-toggleable */}
        <div className="fs-tab-card fs-tab-always-on">
          <div className="fs-tab-card-left">
            <div className="fs-tab-icon" style={{ background: "#667eea18", color: "#667eea" }}>
              <FiActivity size={22} />
            </div>
            <div className="fs-tab-info">
              <div className="fs-tab-label">Overview</div>
              <div className="fs-tab-desc">
                Facility health score, KPIs, DG fuel gauge, AI insights and zone summary.
              </div>
            </div>
          </div>
          <div className="fs-always-on-badge">Always On</div>
        </div>

        {/* Configurable tabs */}
        {CONFIGURABLE_TABS.map(tab => {
          const isOn = enabled.includes(tab.id);
          return (
            <div
              key={tab.id}
              className={`fs-tab-card ${isOn ? "fs-tab-card-on" : "fs-tab-card-off"}`}
              onClick={() => toggle(tab.id)}
            >
              <div className="fs-tab-card-left">
                <div
                  className="fs-tab-icon"
                  style={{ background: `${tab.color}18`, color: isOn ? tab.color : "#94a3b8" }}
                >
                  <tab.icon size={22} />
                </div>
                <div className="fs-tab-info">
                  <div className="fs-tab-label" style={{ color: isOn ? "#1e293b" : "#94a3b8" }}>
                    {tab.label}
                  </div>
                  <div className="fs-tab-desc">{tab.desc || tab.description}</div>
                </div>
              </div>
              <div className={`fs-toggle ${isOn ? "fs-toggle-on" : "fs-toggle-off"}`}
                style={isOn ? { background: tab.color } : {}}>
                <div className="fs-toggle-knob" />
              </div>
            </div>
          );
        })}
      </div>

      {/* Preview strip */}
      <div className="fs-preview">
        <div className="fs-preview-label">Tab bar preview</div>
        <div className="fs-preview-tabs">
          <div className="fs-preview-tab fs-preview-tab-active">
            <FiActivity size={13} />
            <span>Overview</span>
          </div>
          {CONFIGURABLE_TABS.filter(t => enabled.includes(t.id)).map(t => (
            <div key={t.id} className="fs-preview-tab">
              <t.icon size={13} />
              <span>{t.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FacilitySettings;
